import { type Attributes, useContext, useMemo, type JSX } from 'react';

import {
  DEFAULT_BAR_OPACITY,
  STRIPES_PATTERN_ID,
  TIMESTAMP_WIDTH,
} from '../constants/defaults.js';
import { GanttSegmentsContext } from '../context/GanttSegmentsContext.js';
import { getEntryColor } from '../functions/get-entry-color.js';
import { useGanttEntryHandlers } from '../hooks/use-gantt-entry-handlers.js';
import { useGanttSegmentPosition } from '../hooks/use-gantt-segment-position.js';
import type { GanttSegmentData } from '../types/gantt-chart.js';

type GanttSegmentProps = Attributes & {
  containerWidth: number;
  height: number;
  start: number;
  end?: number;
  data?: GanttSegmentData;
};

export const GanttSegment = (props: GanttSegmentProps): JSX.Element => {
  const { start, end, height, containerWidth, data } = props;
  const {
    segmentsData,
    parentSegmentsData,
    config: { colorAccessor, colorPalette },
  } = useContext(GanttSegmentsContext);
  const fill = data
    ? getEntryColor(data, colorAccessor, colorPalette)
    : `url(#${STRIPES_PATTERN_ID})`;

  const dataForEntryHandlers = useMemo(
    () =>
      data
        ? {
            segment: data,
            row: segmentsData,
            parent: parentSegmentsData,
          }
        : undefined,
    [data, segmentsData, parentSegmentsData],
  );
  const {
    onMouseClickHandler,
    onMouseEnterHandler,
    onMouseLeaveHandler,
    onMouseMoveHandler,
  } = useGanttEntryHandlers(dataForEntryHandlers);

  if (
    !isFinite(start) ||
    containerWidth < 0 ||
    (end !== undefined && !isFinite(end))
  ) {
    throw new Error('Invalid input data');
  }

  const {
    start: segmentXStart,
    end: segmentXEnd,
    width: segmentWidth,
    isNegative,
  } = useGanttSegmentPosition(start, end ?? start);

  // render as Timestamp if timestamp or if the width would be smaller than 3
  const isTimestamp =
    end === undefined || start === end || segmentWidth < TIMESTAMP_WIDTH;
  const segmentX = isNegative ? segmentXEnd : segmentXStart;
  const x = isTimestamp ? segmentX - 1 : segmentX;
  const width = isTimestamp ? TIMESTAMP_WIDTH : segmentWidth;

  return (
    <rect
      onMouseEnter={onMouseEnterHandler}
      onClick={onMouseClickHandler}
      onMouseLeave={onMouseLeaveHandler}
      onMouseMove={onMouseMoveHandler}
      x={x}
      y={0}
      opacity={DEFAULT_BAR_OPACITY}
      width={width}
      height={height}
      fill={fill}
      pointerEvents="auto"
    />
  );
};
